// ./src/components/features/EventGuests/AddressAnswerInput.tsx
//
// An ADDRESS answer, filled in on a guest's behalf (ROADMAP A2e #86): the street with Google autocomplete, then the
// apartment line, city, state / province, postal code and country — the contact editor's address block, as one control.
// A PICK — and only a pick, no guess on blur — fills the other parts (region and country arrive as short codes, "ON" /
// "CA", as the contact editor stores them); every part stays editable. The SERVER owns the rule (a street, a city and a
// country, or blank) and derives the one-line copy every report prints; this control only outlines the empty part when
// the editor reports an error.

import React, { useRef } from "react";
import { Group, Input, Stack, TextInput } from "@mantine/core";
import {
  ADDRESS_PART_LABEL,
  ADDRESS_REQUIRED_PARTS,
  addressFromPlace,
  addressIsEmpty,
  addressMissingParts,
  toAnswerAddress,
  type AddressPart,
  type AnswerAddress,
} from "@em/api";

import { AddressAutocompleteInput } from "@/components/generic";

export interface AddressAnswerInputProps {
  label: string;
  description?: string;
  required?: boolean;
  value: unknown;
  onChange: (next: AnswerAddress) => void;
  /** The question's error; the empty parts behind it are outlined too. */
  error?: string;
}

export const AddressAnswerInput: React.FC<AddressAnswerInputProps> = ({ label, description, required, value, onChange, error }) => {
  const addr = toAnswerAddress(value);
  // The address as it is NOW — a pick resolves asynchronously, after the host may have typed in another box.
  const latest = useRef(addr);
  latest.current = addr;
  const empty = addressIsEmpty(addr);
  const flagged = new Set<AddressPart>(!error ? [] : empty ? (required ? ADDRESS_REQUIRED_PARTS : []) : addressMissingParts(addr));

  const part = (key: AddressPart) => (
    <TextInput
      size="md"
      label={ADDRESS_PART_LABEL[key]}
      value={addr[key]}
      onChange={(e) => {
        // ⚠ Read before the update — never inside it (memory: no event read in a state updater).
        const next = e.currentTarget.value;
        onChange({ ...addr, [key]: next });
      }}
      error={flagged.has(key)}
    />
  );

  return (
    <Input.Wrapper size="md" label={label} description={description} withAsterisk={required} error={error}>
      <Stack gap="xs" mt={6} mb={error ? 6 : 0}>
        <AddressAutocompleteInput
          size="md"
          label={ADDRESS_PART_LABEL.streetAddress}
          value={addr.streetAddress}
          onChange={(street, picked) => {
            const current = latest.current;
            onChange(picked ? addressFromPlace(picked, current, street) : { ...current, streetAddress: street });
          }}
          error={flagged.has("streetAddress")}
          withinPortal
          // The full-screen picker on a phone, as every picker is (its Esc closes it alone — it is on the overlay
          // stack). Picks only — a blur guess would overwrite the city typed below. No "use my location": it is the
          // GUEST's address, not the host's.
          geocodeOnBlur={false}
          showLocateButton={false}
        />
        {part("extraAddress")}
        <Group grow align="flex-start">
          {part("city")}
          {part("stateProvince")}
        </Group>
        <Group grow align="flex-start">
          {part("postalCode")}
          {part("country")}
        </Group>
      </Stack>
    </Input.Wrapper>
  );
};
